import { useState, useCallback, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import { 
  parseApiError, 
  StandardizedError,
  isRetryableError 
} from '../utils/consolidatedErrorHandling.js';

/**
 * Enhanced error handler specifically for analytics features
 * Provides retry mechanisms, user-friendly error messages, and fallback displays
 */
const useAnalyticsErrorHandler = () => {
  const { showError, showWarning, showInfo } = useToast();
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const retryTimeoutRef = useRef(null);

  /**
   * Handle analytics API errors with enhanced error parsing and user feedback
   */
  const handleError = useCallback(async (error, operation, context = {}) => {
    let structuredError;

    // Parse the error based on its type
    if (error.response) {
      // API response error
      structuredError = await parseApiError(error.response);
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      // Network error
      structuredError = {
        code: 'NETWORK_ERROR',
        message: 'Unable to connect to the server. Please check your internet connection.',
        canRetry: true,
        category: 'network',
        severity: 'warning'
      };
    } else if (error.name === 'AbortError') {
      // Request was cancelled
      structuredError = {
        code: 'REQUEST_CANCELLED',
        message: 'Request was cancelled',
        canRetry: false,
        category: 'user_action',
        severity: 'info'
      };
    } else {
      // Generic error
      structuredError = {
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unexpected error occurred',
        canRetry: false,
        category: 'general',
        severity: 'error'
      };
    }

    // Add operation context
    structuredError.operation = operation;
    structuredError.context = context;
    structuredError.timestamp = new Date().toISOString();

    // Format for display
    const displayError = {
      message: structuredError.message,
      type: structuredError.category || 'error',
      details: structuredError.details
    };
    setError(displayError);

    // Show appropriate toast notification
    const toastMessage = getToastMessage(displayError, operation);
    switch (displayError.severity) {
      case 'critical':
      case 'error':
        showError(toastMessage, 8000); // Longer duration for errors
        break;
      case 'warning':
        showWarning(toastMessage, 6000);
        break;
      case 'info':
        showInfo(toastMessage, 4000);
        break;
    }

    // Log error for debugging
    console.error(`Analytics error in ${operation}:`, {
      originalError: error,
      structuredError,
      displayError,
      context
    });

    return displayError;
  }, [showError, showWarning, showInfo]);

  /**
   * Retry the failed operation with exponential backoff
   */
  const retry = useCallback(async (retryFunction, maxRetries = 3) => {
    if (isRetrying || retryCount >= maxRetries) {
      return;
    }

    setIsRetrying(true);
    const currentRetryCount = retryCount + 1;
    setRetryCount(currentRetryCount);

    try {
      // Clear previous error
      setError(null);

      // Calculate delay with exponential backoff and jitter
      const baseDelay = 1000; // 1 second
      const delay = Math.min(
        baseDelay * Math.pow(2, currentRetryCount - 1) + Math.random() * 1000,
        10000 // Max 10 seconds
      );

      // Show retry notification
      showInfo(`Retrying in ${Math.ceil(delay / 1000)} seconds...`, delay);

      // Wait before retrying
      await new Promise(resolve => {
        retryTimeoutRef.current = setTimeout(resolve, delay);
      });

      // Execute retry function
      await retryFunction();

      // Reset retry count on success
      setRetryCount(0);
      showInfo('Successfully loaded data', 3000);

    } catch (retryError) {
      // Handle retry failure
      await handleError(retryError, 'retry', { 
        retryAttempt: currentRetryCount,
        maxRetries 
      });

      if (currentRetryCount >= maxRetries) {
        showError('Maximum retry attempts reached. Please try again later.', 10000);
      }
    } finally {
      setIsRetrying(false);
    }
  }, [retryCount, isRetrying, handleError, showInfo, showError]);

  /**
   * Clear current error state
   */
  const clearError = useCallback(() => {
    setError(null);
    setRetryCount(0);
    setIsRetrying(false);
    
    // Clear any pending retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  /**
   * Check if the current error is retryable
   */
  const canRetry = useCallback(() => {
    return error?.canRetry && retryCount < 3 && !isRetrying;
  }, [error, retryCount, isRetrying]);

  /**
   * Get retry delay in seconds
   */
  const getRetryDelay = useCallback(() => {
    if (!canRetry()) return 0;
    
    const baseDelay = 1000;
    return Math.ceil((baseDelay * Math.pow(2, retryCount)) / 1000);
  }, [canRetry, retryCount]);

  return {
    error,
    retryCount,
    isRetrying,
    handleError,
    retry,
    clearError,
    canRetry,
    getRetryDelay
  };
};

/**
 * Generate user-friendly toast messages based on error and operation
 */
const getToastMessage = (error, operation) => {
  const operationLabels = {
    'fetch_analytics': 'loading analytics data',
    'fetch_call_success': 'loading call success data',
    'fetch_conversations': 'loading conversations',
    'fetch_system_health': 'loading system health data',
    'update_conversation_status': 'updating conversation status',
    'delete_conversation': 'deleting conversation',
    'export_analytics': 'exporting analytics data'
  };

  const operationLabel = operationLabels[operation] || operation;

  switch (error.category) {
    case 'network':
      return `Network error while ${operationLabel}. Please check your connection.`;
    
    case 'authentication':
      return `Authentication error while ${operationLabel}. Please sign in again.`;
    
    case 'external_service':
      if (error.canRetry) {
        return `External service temporarily unavailable while ${operationLabel}. Retrying...`;
      }
      return `External service error while ${operationLabel}. Please try again later.`;
    
    case 'rate_limiting':
      return `Too many requests while ${operationLabel}. Please wait a moment.`;
    
    case 'validation':
      return `Invalid data while ${operationLabel}. Please check your input.`;
    
    case 'system':
      return `System error while ${operationLabel}. Our team has been notified.`;
    
    default:
      if (error.canRetry) {
        return `Temporary error while ${operationLabel}. Retrying automatically...`;
      }
      return `Error while ${operationLabel}. Please try again.`;
  }
};

export default useAnalyticsErrorHandler;
